import React, { useState, useEffect } from 'react';
import { useTheme } from "../ThemeContext";
import { 
  Alert, Button, Form, Row, Col, Dropdown, 
  Card, Badge, Modal, Spinner, Container, ListGroup,
  OverlayTrigger, Tooltip, ButtonGroup
} from 'react-bootstrap';
import { 
  FaThumbsUp, FaThumbsDown, FaArrowLeft, FaArrowRight, 
  FaPlus, FaComments, FaUser, FaTag, FaCheck, FaTrash,
  FaPaperPlane, FaLightbulb, FaUsers, FaUserCheck, FaExchangeAlt,
  FaSearch, FaFilter, FaSort, FaSync, FaObjectGroup, FaTimes,
  FaCalendar, FaClock
} from 'react-icons/fa';
import { getAdminToken } from '../services/authService';
import './FeatureRequestBoard.css';
import { TopbarSlot } from '../v2/components';

const FeatureRequestBoard = () => {
  const [newRequest, setNewRequest] = useState({ 
    title: '', 
    description: '', 
    category: '', 
    priority: 'medium',
    tags: [] 
  });
  const [requests, setRequests] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [assignee, setAssignee] = useState('');
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [ageFilter, setAgeFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [requestNotes, setRequestNotes] = useState({});
  const [admins, setAdmins] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const { isDarkMode } = useTheme();
  const token = getAdminToken();

  const themeClass = isDarkMode ? 'bg-dark text-light placeholder-light' : 'bg-light text-dark';
  const cardClass = isDarkMode ? 'bg-dark-2 text-light' : 'light-card';
  const btnVariant = isDarkMode ? 'outline-light' : 'outline-dark';

  // Categories for filtering
  const categories = ['UI/UX', 'Functionality', 'Performance', 'Security', 'Bug', 'Other'];
  const priorities = ['low', 'medium', 'high', 'critical'];
  const ageOptions = [
    { value: '', label: 'All Time' },
    { value: '1d', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];


  // Helper functions for date formatting
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
    return `${Math.floor(diffInSeconds / 31536000)}y ago`;
  };

  // Fetch requests with filters
  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/s1/admin/requests/${currentPage}?`;
      
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterCategory) url += `category=${encodeURIComponent(filterCategory)}&`;
      if (sortBy) url += `sort=${sortBy}&`;
      if (ageFilter) url += `age=${encodeURIComponent(ageFilter)}&`;
      
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setRequests(data.results || []);
      else setError(data.error || 'Error fetching requests');
    } catch (err) {
      setError('Network error or API issue');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const res = await fetch('/s1/admin/admins', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setAdmins(data || []);
    } catch (err) {
      console.error("Error fetching admins:", err);
    }
  };

  useEffect(() => { 
    fetchRequests(); 
  }, [currentPage, searchTerm, filterCategory, sortBy, ageFilter, showArchived]);

  useEffect(() => {
    fetchAdmins();
  }, []);


  const handleVote = async (id, type) => {
    setVoting(id);
    try {
      const res = await fetch(`/s1/admin/requests/${id}/vote`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ voteType: type }),
      });
      const data = await res.json();
      if (res.ok) {
        setRequests((prev) => prev.map(r => r.id === id ? { ...r, votes: data.request.votes } : r));
      } else setError(data.error || "Failed to submit vote");
    } catch (err) { setError(err.message); }
    setVoting(false);
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await fetch(`/s1/admin/requests/${id}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (res.ok) {
        setRequests(prev => prev.map(req => 
          req.id === id ? { ...req, status: data.updatedRequest.status } : req
        ));
        setMessage(`Status updated to ${status}`);
      } else {
        setError(data.error || "Failed to update status");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNote = async (id) => {
    if (!noteContent.trim()) return;
    
    try {
      const res = await fetch(`/s1/admin/requests/${id}/notes`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ note: noteContent }),
      });
      const data = await res.json();
      if (res.ok) {
        setNoteContent('');
        setShowModal(false);
        setMessage("Note added successfully");
        
        // Refresh notes for this request if it's expanded
        if (expandedRequest === id) {
          fetchNotes(id);
        }
        
        fetchRequests(); // Refresh to get updated notes count
      } else {
        setError(data.error || "Failed to add note");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchNotes = async (id) => {
    try {
      const res = await fetch(`/s1/admin/requests/${id}/notes`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setRequestNotes(prev => ({
          ...prev,
          [id]: data.notes || []
        }));
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
    }
  };

  const handleToggleNotes = (id) => {
    if (expandedRequest === id) {
      setExpandedRequest(null);
    } else {
      setExpandedRequest(id);
      if (!requestNotes[id]) {
        fetchNotes(id);
      }
    }
  };

  const handleAssign = async (id, assigneeId) => {
    try {
      const res = await fetch(`/s1/admin/requests/${id}/assign`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ assignee: assigneeId }),
      });
      const data = await res.json();
      if (res.ok) {
        setRequests(prev => prev.map(req => 
          req.id === id ? { 
            ...req, 
            assigned_to: data.updatedRequest.assigned_to,
            assignee_name: data.updatedRequest.assignee_name 
          } : req
        ));
        setMessage(data.updatedRequest.assignee_name ? `Assigned to ${data.updatedRequest.assignee_name}` : 'Request unassigned');
      } else {
        setError(data.error || "Failed to assign");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this feature request?")) return;
    
    try {
      const res = await fetch(`/s1/admin/requests/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMessage("Feature request deleted");
        fetchRequests();
        // Remove from selected if it was selected
        setSelectedRequests(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      } else {
        const data = await res.json();
        setError(data.error || "Failed to delete");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMerge = async () => {
    if (selectedRequests.size < 2) {
      setError("Please select at least 2 requests to merge");
      return;
    }
    
    const requestIds = Array.from(selectedRequests);
    const mainRequestId = requestIds[0]; // Use the first selected as main
    const mergeRequestIds = requestIds.slice(1);
    
    try {
      const res = await fetch(`/s1/admin/requests/merge`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          main_request_id: mainRequestId, 
          merge_request_ids: mergeRequestIds 
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`Successfully merged ${mergeRequestIds.length} requests`);
        setSelectedRequests(new Set());
        fetchRequests();
      } else {
        setError(data.error || "Failed to merge requests");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleRequestSelection = (id) => {
    setSelectedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const nextPage = () => setCurrentPage((p) => p + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  useEffect(() => {
    const timer = setTimeout(() => { setError(null); setMessage(null); }, 4000);
    return () => clearTimeout(timer);
  }, [error, message]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <Container fluid className="p-0">
      <div className="feature-request-board" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <TopbarSlot>
          <Button variant="outline-primary" size="sm" onClick={fetchRequests} className="d-flex align-items-center">
            <FaSync className="me-1" /> Refresh
          </Button>
        </TopbarSlot>

        {/* Filters and Search */}
        <Card className={`mb-4 mt-2 shadow-sm ${cardClass}`}>
          <Card.Header 
            className="d-flex align-items-center justify-content-between cursor-pointer"
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className="d-flex align-items-center">
              <FaFilter className="me-2" />
              <h6 className="mb-0">Filters & Search</h6>
            </div>
            <span>{showFilters ? '▲' : '▼'}</span>
          </Card.Header>
          {showFilters && (
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Search</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        placeholder="Search feature requests..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`modern-input ps-4 ${themeClass}`}
                      />
                      <FaSearch className={`position-absolute top-50 start-0 translate-middle-y ms-2 ${themeClass}` }/>
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className={`modern-input ps-4 ${themeClass}`}
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Age</Form.Label>
                    <Form.Select
                      value={ageFilter}
                      onChange={(e) => setAgeFilter(e.target.value)}
                      className={`modern-input ps-4 ${themeClass}`}
                    >
                      {ageOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sort By</Form.Label>
                    <Form.Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className={`modern-input ps-4 ${themeClass}`}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="votes">Most Votes</option>
                      <option value="title">Title</option>
                      <option value="category">Category</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          )}
        </Card>

        {/* Merge Button (shown when multiple requests selected) */}
        {selectedRequests.size > 1 && (
          <div className="mb-3 p-3 bg-primary text-white rounded shadow d-flex align-items-center justify-content-between">
            <div>
              <FaObjectGroup className="me-2" />
              <strong>{selectedRequests.size} requests selected</strong>
            </div>
            <ButtonGroup>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Merge selected requests into one</Tooltip>}
              >
                <Button variant="light" onClick={handleMerge}>
                  <FaExchangeAlt className="me-1" /> Merge Requests
                </Button>
              </OverlayTrigger>
              <Button 
                variant="outline-light" 
                onClick={() => setSelectedRequests(new Set())}
              >
                <FaTimes /> Clear
              </Button>
            </ButtonGroup>
          </div>
        )}

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant={isDarkMode ? "light" : "dark"} />
            <p className="mt-2">Loading feature requests...</p>
          </div>
        ) : (
          <>
            {requests.length === 0 ? (
              <Card className={`text-center py-5 ${cardClass}`}>
                <Card.Body>
                  <FaLightbulb size={48} className="text-muted mb-3" />
                  <h5>No feature requests found</h5>
                  <p className="text-muted">Try adjusting your filters or create the first request!</p>
                </Card.Body>
              </Card>
            ) : (
              requests.map((req) => (
                <Card 
                  key={req.id} 
                  className={`mb-4 mt-2 p-3 shadow-sm ${cardClass} feature-card ${selectedRequests.has(req.id) ? 'selected-card' : ''} ${openDropdownId === req.id ? 'active-dropdown-card' : ''}`}
                >
                  <Card.Body>
                    <Row>
                      <Col md={1} className="d-flex align-items-start justify-content-center">
                        <Form.Check
                          type="checkbox"
                          checked={selectedRequests.has(req.id)}
                          onChange={() => toggleRequestSelection(req.id)}
                          className="mt-1"
                        />
                      </Col>
                      <Col md={7}>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 style={{color:isDarkMode?"white":'gray'}} className="feature-title">{req.title}</h5>
                          <Badge bg={getPriorityColor(req.priority)} className="text-capitalize">
                            {req.priority}
                          </Badge>
                        </div>
                        
                        <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
                          <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaUser className="me-1" /> {req.submitter || 'Unknown'}
                          </Badge>
                          <Badge 
                            bg={
                              req.status === 'released' ? 'success' : 
                              req.status === 'in_progress' ? 'primary' : 
                              req.status === 'planned' ? 'info' : 'secondary'
                            } 
                            className="d-flex align-items-center text-capitalize"
                          >
                            <FaTag className="me-1" /> {req.status?.replace('_', ' ') || 'new'}
                          </Badge>
                          <Badge bg="secondary" className="d-flex align-items-center">
                            {req.category}
                          </Badge>
                          <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaCalendar className="me-1" size={10} /> {formatDate(req.created_at)}
                          </Badge>
                          <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaClock className="me-1" size={10} /> Updated {getTimeAgo(req.updated_at || req.created_at)}
                          </Badge>
                          {req.assignee_name && (
                            <Badge bg="info" className="d-flex align-items-center">
                              <FaUserCheck className="me-1" /> Assigned to: {req.assignee_name}
                            </Badge>
                          )}
                          {req.tags && req.tags.length > 0 && req.tags.map(tag => (
                            <Badge key={tag} bg="outline-secondary" className="me-1">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        
                        <p className="feature-description">{req.description}</p>
                      </Col>
                      
                      <Col md={4}>
                        <div className="d-grid gap-2">
                          <Dropdown 
                            onToggle={(isOpen) => setOpenDropdownId(isOpen ? req.id : null)}
                            popperConfig={{ strategy: 'fixed' }}
                          >
                            <Dropdown.Toggle variant='outline-primary' size="sm" className="d-flex align-items-center justify-content-between">
                              <span>Change Status</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className={isDarkMode ? 'dropdown-menu-dark' : ''} style={{maxHeight: '300px', overflowY: 'auto'}}>
                              <Dropdown.Item onClick={() => handleStatusChange(req.id, 'new')}>
                                <FaCheck className="me-2 text-secondary" /> New
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStatusChange(req.id, 'under_review')}>
                                <FaCheck className="me-2 text-info" /> Under Review
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStatusChange(req.id, 'planned')}>
                                <FaCheck className="me-2 text-primary" /> Planned
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStatusChange(req.id, 'in_progress')}>
                                <FaCheck className="me-2 text-warning" /> In Progress
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStatusChange(req.id, 'released')}>
                                <FaCheck className="me-2 text-success" /> Released
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          
                          <Dropdown 
                            onToggle={(isOpen) => setOpenDropdownId(isOpen ? req.id : null)}
                            popperConfig={{ strategy: 'fixed' }}
                          >
                            <Dropdown.Toggle variant='outline-secondary' size="sm" className="d-flex align-items-center justify-content-between">
                              <span>{req.assignee_name ? `Assigned to: ${req.assignee_name}` : 'Assign To'}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className={isDarkMode ? 'dropdown-menu-dark' : ''} style={{maxHeight: '300px', overflowY: 'auto'}}>
                              <Dropdown.Item 
                                onClick={() => handleAssign(req.id, null)}
                                className="text-danger"
                              >
                                <FaTimes className="me-2" /> Unassign
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              {admins.length === 0 ? (
                                <Dropdown.Item disabled>No admins found</Dropdown.Item>
                              ) : (
                                admins.map(admin => (
                                  <Dropdown.Item 
                                    key={admin.id} 
                                    onClick={() => handleAssign(req.id, admin.id)}
                                    active={req.assigned_to === admin.id}
                                  >
                                    <div className="d-flex flex-column">
                                      <span>{admin.username}</span>
                                      <small className="text-muted" style={{fontSize: '0.75rem'}}>{admin.email}</small>
                                    </div>
                                  </Dropdown.Item>
                                ))
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                          
                          <Button 
                            variant='outline-warning' 
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(req);
                              setShowModal(true);
                            }}
                            className="d-flex align-items-center justify-content-between"
                          >
                            <span>Add Note</span>
                            <FaComments />
                          </Button>
                          
                          <Button 
                            variant={expandedRequest === req.id ? 'primary' : btnVariant} 
                            size="sm"
                            className="d-flex align-items-center justify-content-between"
                            onClick={() => handleToggleNotes(req.id)}
                          >
                            <span>View Notes ({req.note_count || 0})</span>
                            <FaComments />
                          </Button>
                          
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(req.id)}
                            className="d-flex align-items-center justify-content-between"
                          >
                            <span>Delete</span>
                            <FaTrash />
                          </Button>
                          
                          <div className="d-flex mt-3 align-items-center justify-content-between p-2 rounded">
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              onClick={() => handleVote(req.id, 'up')} 
                              disabled={voting === req.id}
                              className="vote-btn"
                            >
                              <FaThumbsUp />
                            </Button>
                            <span className="mx-2 fw-bold">{req.votes || 0} votes</span>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleVote(req.id, 'down')} 
                              disabled={voting === req.id}
                              className="vote-btn"
                            >
                              <FaThumbsDown />
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Expanded Notes Section */}
                    {expandedRequest === req.id && (
                      <div className={`mt-4 p-3 border-top ${isDarkMode ? 'border-secondary' : ''}`}>
                        <h6 className="mb-3 d-flex align-items-center">
                          <FaComments className="me-2" />
                          Admin Notes ({req.note_count || 0})
                        </h6>
                        
                        {requestNotes[req.id] && requestNotes[req.id].length > 0 ? (
                          <ListGroup variant="flush">
                            {requestNotes[req.id].map((note, idx) => (
                              <ListGroup.Item 
                                key={idx} 
                                className={`${isDarkMode ? 'bg-dark text-light border-secondary' : 'bg-transparent'} py-3 px-0`}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <div className="d-flex align-items-center">
                                    <FaUser className="me-2 text-muted" size={12} />
                                    <strong className="small">{note.author || 'Admin'}</strong>
                                  </div>
                                  <div className="d-flex align-items-center text-muted small">
                                    <FaClock className="me-1" size={10} />
                                    {getTimeAgo(note.created_at)}
                                  </div>
                                </div>
                                <div className="ps-4">
                                  {note.note || note.text}
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <p className="text-muted small text-center py-3">No notes yet for this request.</p>
                        )}
                        
                        <div className="mt-3 text-end">
                          <Button 
                            variant="outline-warning" 
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(req);
                              setShowModal(true);
                            }}
                          >
                            <FaPlus className="me-1" /> Add Note
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))
            )}
          </>
        )}

        {/* Pagination */}
        {requests.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-4">
            <Button 
              variant="outline-primary" 
              onClick={prevPage} 
              disabled={currentPage === 1}
              className="d-flex align-items-center"
            >
              <FaArrowLeft className="me-1" /> Previous
            </Button>
            <span className="fw-bold">Page {currentPage}</span>
            <Button 
              variant="outline-primary" 
              onClick={nextPage} 
              disabled={requests.length < 10}
              className="d-flex align-items-center"
            >
              Next <FaArrowRight className="ms-1" />
            </Button>
          </div>
        )}

        {/* Add Note Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Note to {selectedRequest?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Enter your note here..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => handleAddNote(selectedRequest.id)}>
              Add Note
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default FeatureRequestBoard;